import React, { useState } from 'react';
import { Card, Table, Button, Badge, Form, InputGroup } from 'react-bootstrap';
import { FaEdit, FaTrash, FaCheck, FaPlus } from 'react-icons/fa';

const ACCENT = '#60A5FA';

const DebtsList = ({ debts, onEdit, onDelete, onSettle, onRecordPayment, onAdd }) => {
  const [payAmounts, setPayAmounts] = useState({});

  const openDebts = debts.filter((d) => d.status === 'open' || d.status === 'partial');
  const closedDebts = debts.filter((d) => d.status === 'settled' || d.status === 'written_off');

  const owedToMe = openDebts
    .filter((d) => d.direction === 'receivable')
    .reduce((sum, d) => sum + Number(d.remaining), 0);
  const iOwe = openDebts
    .filter((d) => d.direction === 'payable')
    .reduce((sum, d) => sum + Number(d.remaining), 0);

  const renderRows = (items) =>
    items.map((debt) => (
      <tr key={debt.id}>
        <td>
          <div style={{ fontWeight: 600 }}>{debt.person}</div>
          {debt.reason ? <small style={{ color: '#9ca3af' }}>{debt.reason}</small> : null}
        </td>
        <td>
          <Badge bg={debt.direction === 'receivable' ? 'success' : 'danger'}>
            {debt.direction === 'receivable' ? 'They owe me' : 'I owe them'}
          </Badge>
        </td>
        <td>€{Number(debt.amount).toFixed(2)}</td>
        <td style={{ color: debt.direction === 'receivable' ? '#22c55e' : '#f87171', fontWeight: 600 }}>
          €{Number(debt.remaining).toFixed(2)}
        </td>
        <td>
          <Badge
            bg={
              debt.is_overdue
                ? 'danger'
                : debt.status === 'settled'
                  ? 'success'
                  : debt.status === 'partial'
                    ? 'warning'
                    : 'secondary'
            }
            text={debt.status === 'partial' ? 'dark' : undefined}
          >
            {debt.is_overdue ? 'Overdue' : debt.status}
          </Badge>
        </td>
        <td>{debt.due_date || '—'}</td>
        <td>
          {(debt.status === 'open' || debt.status === 'partial') && (
            <InputGroup size="sm" className="mb-2" style={{ maxWidth: 180 }}>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="Paid €"
                value={payAmounts[debt.id] || ''}
                onChange={(e) =>
                  setPayAmounts((prev) => ({ ...prev, [debt.id]: e.target.value }))
                }
                className="bg-dark text-light border-secondary"
              />
              <Button
                variant="outline-success"
                onClick={() => {
                  const amt = parseFloat(payAmounts[debt.id]);
                  if (!amt || amt <= 0) return;
                  onRecordPayment(debt.id, amt);
                  setPayAmounts((prev) => ({ ...prev, [debt.id]: '' }));
                }}
              >
                Pay
              </Button>
            </InputGroup>
          )}
          <div className="d-flex gap-1 flex-wrap">
            {(debt.status === 'open' || debt.status === 'partial') && (
              <Button size="sm" variant="outline-success" onClick={() => onSettle(debt.id)}>
                <FaCheck className="me-1" /> Settle
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-primary"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onClick={() => onEdit(debt)}
            >
              <FaEdit />
            </Button>
            <Button size="sm" variant="outline-danger" onClick={() => onDelete(debt.id)}>
              <FaTrash />
            </Button>
          </div>
        </td>
      </tr>
    ));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex gap-4">
          <div>
            <div style={{ color: '#9ca3af', fontSize: 13 }}>Owed to me</div>
            <div style={{ color: '#22c55e', fontSize: 22, fontWeight: 700 }}>€{owedToMe.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', fontSize: 13 }}>I owe</div>
            <div style={{ color: '#f87171', fontSize: 22, fontWeight: 700 }}>€{iOwe.toFixed(2)}</div>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={onAdd}
          style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
        >
          <FaPlus className="me-2" /> Add debt
        </Button>
      </div>

      <Card className="bg-dark text-light border-secondary mb-4">
        <Card.Header className="bg-dark border-secondary">Open</Card.Header>
        <Card.Body className="p-0">
          <Table hover variant="dark" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Person / reason</th>
                <th>Direction</th>
                <th>Original</th>
                <th>Remaining</th>
                <th>Status</th>
                <th>Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {openDebts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4" style={{ color: '#9ca3af' }}>
                    No open debts. Track loans and IOUs here.
                  </td>
                </tr>
              ) : (
                renderRows(openDebts)
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {closedDebts.length > 0 && (
        <Card className="bg-dark text-light border-secondary">
          <Card.Header className="bg-dark border-secondary">Settled / closed</Card.Header>
          <Card.Body className="p-0">
            <Table hover variant="dark" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Person / reason</th>
                  <th>Direction</th>
                  <th>Original</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{renderRows(closedDebts)}</tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default DebtsList;
